/**
 * KLOOKPEND-1 · 渠道中心「接口待确认」开关卡（Klook / Civitatis 详情页）。
 *   PATCH /api/v1/integrations/platforms/{code}/allow-pending { enabled, reason? }（只有老板 / 技术管理员；改动写操作日志，原因选填）
 * 打开 = 不满足自动确认条件的订单回 PENDING 转人工（和携程一样）；关闭 = 一律即时确认（现状）。
 * 🔴 打开前必须先确认对方已书面同意接受 PENDING——弹窗二次确认。
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateAllowPending } from "@/services/channelCenter";
import type { PlatformDetail } from "@/services/types/channelCenter";
import { ApiError } from "@/services/api";
import { useAuthStore } from "@/store/authStore";

/** 只有老板 / 技术管理员能改（后端同样 403）。 */
export function canToggleAllowPending(user: { roleCodes?: string[]; role?: string } | null | undefined): boolean {
  const roles = user?.roleCodes?.length ? user.roleCodes : user?.role ? [user.role] : [];
  return roles.includes("owner") || roles.includes("tech_admin");
}

export function PendingSwitchCard({ platform }: { platform: PlatformDetail }) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const can = canToggleAllowPending(user);
  const on = !!platform.allow_pending;
  const [ask, setAsk] = useState<"on" | "off" | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (enabled: boolean) => updateAllowPending(platform.code, { enabled, reason: reason.trim() || undefined }),
    onSuccess: () => {
      setAsk(null); setAgreed(false); setReason(""); setErr(null);
      qc.invalidateQueries({ queryKey: ["channel-center", "platform", platform.code] });
      qc.invalidateQueries({ queryKey: ["channel-center"] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "修改失败，请稍后再试"),
  });

  function open(kind: "on" | "off") {
    setAsk(kind); setAgreed(false); setReason(""); setErr(null);
  }

  return (
    <div className="rounded-[14px] border border-slate-200 bg-white p-5" data-testid="pending-switch-card">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h3 className="text-[15px] font-semibold text-slate-900">接口待确认（不满足自动确认条件时转人工）</h3>
          <div className="mt-1 text-[12px] text-slate-400">
            关闭（默认）：{platform.display_name} 来的订单一律即时确认。打开后：5 个自动确认条件（编码对映射 · 团期在售 · 余位够 · 结算价不低于成本 · 多日游住房数算得出）有一条不满足，
            接口回 PENDING，订单进「待确认」等人处理，24 小时没处理按拒绝处理；多日游产品对外标注为非即时确认。
          </div>
        </div>
        <span data-testid="pending-switch-state" className={`mt-0.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${on ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{on ? "已打开" : "已关闭"}</span>
        <button
          type="button" data-testid="pending-switch-btn" disabled={!can}
          title={can ? undefined : "只有老板 / 技术管理员能改"}
          onClick={() => open(on ? "off" : "on")}
          className="rounded-md border border-slate-200 px-3 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >{on ? "关闭" : "打开"}</button>
      </div>

      {ask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" onClick={() => !mut.isPending && setAsk(null)}>
          <div role="dialog" aria-modal="true" data-testid="pending-switch-modal" onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4">
            <h2 className="text-lg font-semibold text-slate-900">{ask === "on" ? `打开「${platform.display_name} 接口待确认」？` : `关闭「${platform.display_name} 接口待确认」？`}</h2>
            {ask === "on" ? (
              <>
                <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                  请先确认 {platform.display_name} 已书面同意接受 PENDING。对方不接受就打开，订单会卡在 PENDING 上。
                </p>
                <label className="mt-3 flex items-start gap-2 text-[13px] text-slate-700">
                  <input type="checkbox" data-testid="pending-switch-agree" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5" />
                  <span>我已确认 {platform.display_name} 书面同意接受 PENDING</span>
                </label>
              </>
            ) : (
              <p className="mt-2 text-[13px] text-slate-600">关闭后，新来的订单一律即时确认（不再转人工）。已经在「待确认」里的单照常处理。</p>
            )}
            <input aria-label="原因（选填）" data-testid="pending-switch-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="原因（选填，进操作日志）"
              className="mt-3 h-9 w-full rounded-md border border-slate-300 px-3 text-[13px]" />
            {err && <p role="alert" className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setAsk(null)} disabled={mut.isPending} className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">取消</button>
              <button
                type="button" data-testid="pending-switch-confirm" disabled={mut.isPending || (ask === "on" && !agreed)} onClick={() => mut.mutate(ask === "on")}
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >{mut.isPending ? "提交中…" : ask === "on" ? "确认打开" : "确认关闭"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
